import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { IMasterSuiteTouch } from 'src/app/models/mastersuites/mastersuite';
import {
  IAffectedProduct,
  ITouchMapping,
  ITransferPrepareResult,
  TouchMappingAction,
} from 'src/app/models/products/actions/settings/transferProductSettings';

@Component({
  selector: 'app-touch-mapping-table',
  templateUrl: './touch-mapping-table.component.html',
  styleUrls: ['./touch-mapping-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class TouchMappingTableComponent implements OnInit, OnChanges {
  @Input() prepareResult!: ITransferPrepareResult;
  @Output() mappingsChanged = new EventEmitter<ITouchMapping[]>();

  readonly TouchMappingAction = TouchMappingAction;
  mappings: ITouchMapping[] = [];

  ngOnInit(): void {
    if (this.prepareResult) {
      this.mappings = this.buildInitialMappings();
      this.mappingsChanged.emit(this.mappings);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['prepareResult'] && !changes['prepareResult'].firstChange && this.prepareResult) {
      this.mappings = this.buildInitialMappings();
      this.mappingsChanged.emit(this.mappings);
    }
  }

  get sourceTouches(): IMasterSuiteTouch[] {
    return this.prepareResult?.masterSuiteSource?.masterSuiteTouches ?? [];
  }

  get destinationTouches(): IMasterSuiteTouch[] {
    return this.prepareResult?.masterSuiteDestination?.masterSuiteTouches ?? [];
  }

  get sourceMasterSuiteName(): string {
    return this.prepareResult?.masterSuiteSource?.name ?? '';
  }

  get destinationMasterSuiteName(): string {
    return this.prepareResult?.masterSuiteDestination?.name ?? '';
  }

  sourceForDestination(destinationTouchId: string): string | null {
    const mapping = this.mappings.find(
      (m) => m.action === TouchMappingAction.Map && m.destinationTouchId === destinationTouchId,
    );
    return mapping?.sourceTouchId ?? null;
  }

  setSourceForDestination(destinationTouchId: string, sourceTouchId: string | null): void {
    this.mappings = this.mappings.map((mapping) => {
      if (sourceTouchId && mapping.sourceTouchId === sourceTouchId) {
        return { ...mapping, action: TouchMappingAction.Map, destinationTouchId };
      }
      if (mapping.action === TouchMappingAction.Map && mapping.destinationTouchId === destinationTouchId) {
        return { ...mapping, action: TouchMappingAction.Drop, destinationTouchId: undefined };
      }
      return mapping;
    });
    this.mappingsChanged.emit(this.mappings);
  }

  affectedProductsFor(sourceTouchId: string): IAffectedProduct[] {
    return this.prepareResult?.affectedProductsBySourceTouchId?.[sourceTouchId] ?? [];
  }

  affectedCount(sourceTouchId: string): number {
    return this.affectedProductsFor(sourceTouchId).length;
  }

  shootAssetCount(sourceTouchId: string): number {
    return this.affectedProductsFor(sourceTouchId).reduce((total, product) => total + (product.shootAssetCount ?? 0), 0);
  }

  postAssetCount(sourceTouchId: string): number {
    return this.affectedProductsFor(sourceTouchId).reduce((total, product) => total + (product.postAssetCount ?? 0), 0);
  }

  touchLabel(touch: IMasterSuiteTouch): string {
    return `(touch ${touch.index / 1000 + 1}) ${touch.name} (package) ${touch.packageName}`;
  }

  get droppedSourceTouches(): IMasterSuiteTouch[] {
    const droppedIds = new Set(
      this.mappings.filter((mapping) => mapping.action === TouchMappingAction.Drop).map((mapping) => mapping.sourceTouchId),
    );
    return this.sourceTouches
      .filter((touch) => droppedIds.has(touch.id!))
      .sort((a, b) => a.index - b.index);
  }

  get unusedDestinationTouches(): IMasterSuiteTouch[] {
    const claimedIds = new Set(
      this.mappings
        .filter((mapping) => mapping.action === TouchMappingAction.Map && !!mapping.destinationTouchId)
        .map((mapping) => mapping.destinationTouchId!),
    );
    return this.destinationTouches
      .filter((touch) => !claimedIds.has(touch.id!))
      .sort((a, b) => a.index - b.index);
  }

  private buildInitialMappings(): ITouchMapping[] {
    const suggestions = this.prepareResult.suggestedDestinationTouchIdBySourceTouchId ?? {};
    return this.sourceTouches.map((sourceTouch) => {
      const destinationTouchId = suggestions[sourceTouch.id!];
      return {
        sourceTouchId: sourceTouch.id!,
        action: destinationTouchId ? TouchMappingAction.Map : TouchMappingAction.Drop,
        destinationTouchId,
      };
    });
  }
}
